import React, { memo, useCallback } from 'react'
import { Box, Divider, Grid, Stack, Typography } from '@mui/material'
import { Security } from '../../proto/generated/security'
import { OverviewWidgetResponse_Field, OverviewWidgetResponse_FieldGroup } from '../../proto/generated/overview-widget'
import OverviewValue from '../screener/OverviewValue'
import { makeStyles } from 'tss-react/mui'
import ScreenerOverviewRangeBlock from './ScreenerOverviewRangeBlock'
import OverviewPerformanceSection from './OverviewPerformanceSection'

const useStyles = makeStyles()((theme) => ({
    stickyHeader: {
        zIndex: 99
    },
    headerLabel: {
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'uppercase',
        background: theme.palette.background.paper
    },
    gridContainer: {
        padding: theme.spacing(2)
    },
    fieldName: {
        color: theme.palette.text.secondary,
        fontSize: '10px',
        textTransform: 'uppercase'
    }
}))

export type SecurityOverviewFieldGroupsProps = {
    fieldGroups: OverviewWidgetResponse_FieldGroup[]
    currentVisibleOverviewSecurity: Security | undefined
    isOnScreener: boolean
    screenerTargetKey: string | null
    overviewSecurityKey: string | undefined
    securityForContent: Security | undefined
}

const renderFieldGroupsAreEqual = (prev: SecurityOverviewFieldGroupsProps, next: SecurityOverviewFieldGroupsProps) =>
    prev.fieldGroups === next.fieldGroups &&
    prev.currentVisibleOverviewSecurity === next.currentVisibleOverviewSecurity &&
    prev.isOnScreener === next.isOnScreener &&
    prev.screenerTargetKey === next.screenerTargetKey &&
    prev.overviewSecurityKey === next.overviewSecurityKey &&
    prev.securityForContent === next.securityForContent

const SecurityOverviewFieldGroups = ({
    fieldGroups,
    currentVisibleOverviewSecurity,
    isOnScreener,
    screenerTargetKey,
    overviewSecurityKey,
    securityForContent
}: SecurityOverviewFieldGroupsProps) => {
    const { classes, cx } = useStyles()

    const scrollToGivenHeader = useCallback((headerName: string) => {
        const stickyContainer = document.getElementById(headerName)
        const parentContainer = document.getElementById('scrollableDiv')
        const headerHeight = stickyContainer?.parentElement?.offsetHeight || 0
        const parentHeight = parentContainer?.scrollHeight || 0
        const maxScrollTop = parentHeight - headerHeight

        if (maxScrollTop > 0) {
            parentContainer?.scrollTo({
                top: maxScrollTop,
                behavior: 'auto'
            })
        }
    }, [])

    return (
        <>
            {fieldGroups.length > 0 &&
                fieldGroups.map((fieldGroup: OverviewWidgetResponse_FieldGroup) => (
                    <Box key={fieldGroup.name} boxShadow={0}>
                        <Box onClick={() => scrollToGivenHeader(fieldGroup.name)} className={cx(classes.stickyHeader, 'sticky')}>
                            <Divider />
                            <Typography id={fieldGroup.name} className={classes.headerLabel}>
                                {fieldGroup.name}
                            </Typography>
                        </Box>

                        <Grid container rowSpacing={1.75} className={classes.gridContainer}>
                            {fieldGroup.fields.map((field: OverviewWidgetResponse_Field) => (
                                <Grid item xs={6} key={field.name}>
                                    <Stack>
                                        <Typography variant="caption" className={classes.fieldName}>
                                            {field.name}
                                        </Typography>
                                        <OverviewValue overview={true} fieldData={field.screenerField} text={field.value} />
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>

                        {fieldGroup.name.toLowerCase() === 'performance' && currentVisibleOverviewSecurity && (
                            isOnScreener ? (
                                <ScreenerOverviewRangeBlock
                                    securityForContent={securityForContent}
                                    displaySecurity={currentVisibleOverviewSecurity}
                                    screenerTargetKey={screenerTargetKey}
                                    overviewSecurityKey={overviewSecurityKey}
                                    fieldGroups={fieldGroups}
                                />
                            ) : (
                                <OverviewPerformanceSection
                                    security={currentVisibleOverviewSecurity}
                                    fieldGroups={fieldGroups}
                                />
                            )
                        )}
                    </Box>
                ))}
        </>
    )
}

export default memo(SecurityOverviewFieldGroups, renderFieldGroupsAreEqual)
